import {
  Component,
  EventEmitter,
  Output,
  ChangeDetectionStrategy,
  ElementRef,
  Input,
  inject,
} from '@angular/core';
import { InputFieldComponent } from '../../../../../../../../components/ui/inputField/inputField.component';
import { AddTagsModalComponent } from '../../../../../../../../components/tables/add-tags-modal/add-tags-modal.component';
import { ITag } from '../../../../../../../../types/contactModule';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { BroadcastMessageService } from '../../../../../../../../services/broadcast/broadcast.service';

/**
 * Second step of "save these numbers": add them to tags that exist, or to a
 * new tag made here. The parent sends the message with that choice.
 */
@Component({
  selector: 'save-phone-number-to-tag',
  standalone: true,
  imports: [InputFieldComponent, AddTagsModalComponent, ReactiveFormsModule],
  templateUrl: './save-phone-number-to-tag.component.html',
  styleUrl: './save-phone-number-to-tag.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush, // Recommended for performance
})
export class SavePhoneNumberToTagComponent {
  broadcastService = inject(BroadcastMessageService);
  private host: ElementRef<HTMLElement> = inject(ElementRef);

  @Output() goToPrevious = new EventEmitter<void>();
  @Output() selectedTagsChange = new EventEmitter<ITag[]>(); // New output
  @Output() submitEvent = new EventEmitter(); // submit an event when the submit button is hit.
  @Output() updateSaveToExistingTagsEvents = new EventEmitter<boolean>();

  // receive the new tag form from the parent component
  @Input({ required: true }) newTagForm!: FormGroup<{
    name: FormControl<string | null>;
    description: FormControl<string | null>;
  }>;
  @Input({ required: true }) submitted!: boolean;
  /** The send is in flight (an input, so this OnPush view updates). */
  @Input() loading = false;
  /** Why the last attempt failed, from the parent. */
  @Input() problem = '';
  /** Say "schedule" instead of "send" on the button. */
  @Input() scheduled = false;

  /** False: add to existing tags. True: create a new tag. */
  checked = false;

  // Private backing field
  private _selectedTags: ITag[] = [];

  // Public getter
  get selectedTags(): ITag[] {
    return this._selectedTags;
  }

  // Setter that emits changes
  set selectedTags(tags: ITag[]) {
    if (this._selectedTags === tags) return; // Prevent unnecessary emissions

    this._selectedTags = tags || [];
    this.selectedTagsChange.emit(this._selectedTags);
  }

  get selectedTagsCount(): number {
    return this._selectedTags.length;
  }

  toggleViewSelection() {
    this.checked = !this.checked;
    this.updateSaveToExistingTagsEvents.emit(!this.checked);
  }

  /** Radio change: switch only when the choice actually changes. */
  setMode(createNew: boolean) {
    if (this.checked !== createNew) this.toggleViewSelection();
  }

  // Tags Modal
  showAddTagsModal = false;

  openAddTagsModal() {
    this.showAddTagsModal = true;
  }

  closeAddTagsModal() {
    this.showAddTagsModal = false;
  }

  goToPreviousView() {
    this.goToPrevious.emit();
  }

  // Called from AddTagsModalComponent when tags are confirmed
  addSelectedTags(tags: ITag[]) {
    this.selectedTags = tags; // Triggers setter → emits
  }

  removeTag(tagToRemove: ITag, event?: Event) {
    const pressed = event?.currentTarget as HTMLElement | null;
    const index = Math.max(0, this._selectedTags.indexOf(tagToRemove));
    this.selectedTags = this._selectedTags.filter(
      (t) => t.id !== tagToRemove.id
    );
    if (!pressed) return;
    // The pressed button goes with its tag: focus the next tag's remove button, else "Choose tags".
    setTimeout(() => {
      const root = this.host.nativeElement;
      const buttons = Array.from(root.querySelectorAll<HTMLElement>('.stt__remove'));
      (buttons[Math.min(index, buttons.length - 1)] ?? root.querySelector<HTMLElement>('.stt__choose'))?.focus();
    });
  }

  removeSelectedTags(tagsToRemove: ITag[]) {
    const idsToRemove = new Set(tagsToRemove.map((t) => t.id));
    this.selectedTags = this._selectedTags.filter(
      (t) => !idsToRemove.has(t.id)
    );
  }

  ngOnChanges() {
    this.selectedTagsChange.emit(this.selectedTags);
  }

  handleSubmit() {
    this.submitEvent.emit();
  }
}
